import _ from 'lodash'
import {DEFAULT_SERIALIZER_TYPE, SCHEMA_PATH_TO_CSS_PROPERTIES} from './metadata'
import {ADDITIONAL_METADATA, type PropertyMetadata} from '../../../metadata'
import serializers from './serializers'
import type {
    ComponentExportStructure,
    VariantSplit,
    ResolvedDataItem,
    WmlFileContent
} from '@wix/document-services-types'
import {
    CSS_DEFAULT_DECLARATION_PROPERTY_FORMAT_RESERVED_CHARS,
    CSS_DELIMITER,
    CssAst,
    CssAtRule,
    CssDeclarationBlock,
    CssFile,
    CssProperty,
    CssRule,
    CssValue,
    DATA_TYPES,
    AT_RULE_QUERY_AST_TO_CSS_MAP,
    VARIANTS_DELIMITER,
    DEFAULT_VARIANT_KEY
} from './constants'
import {isCssRule} from './utils'
import {CssFileBuilder} from './cssFileBuilder'
import type {PartialComponentExport} from '../constants'
import {createCharReplacementFunc} from '../../utils/utils'
import {forEachDataItemProperty, type ForEachObjectLeafCbArgs, stripHashIfExists} from '../../../utils/dataUtils'
import type {VariantData, VariantsData, VariantsMap, VariantType} from '../../../types'

interface DataItemConditions {
    atRule?: CssAtRule
    variants: string[]
}

type CssDeclarationBlockByVariantMap = Record<string, CssDeclarationBlock>

type AtRulesMap = Record<string, CssAtRule>

const replaceReservedPropertyChars = createCharReplacementFunc(CSS_DEFAULT_DECLARATION_PROPERTY_FORMAT_RESERVED_CHARS)

const buildDefaultCssProperty = (namespace: string, pathArr: string[]): string =>
    [namespace, ...pathArr].map(replaceReservedPropertyChars).join(CSS_DELIMITER)

const getFallbackValue = (value: any): CssValue => {
    return serializers[DEFAULT_SERIALIZER_TYPE].serialize(value)
}

const serializeValue = (value: any, types: PropertyMetadata['types']): CssValue => {
    for (const typeMetadata of types) {
        const {type} = typeMetadata
        const serializer = (serializers as Record<string, any>)[type]
        const serializedValue = serializer.serialize(
            value,
            {...typeMetadata, ...ADDITIONAL_METADATA[type]},
            serializeValue
        )

        if (serializedValue !== undefined) {
            return serializedValue
        }
    }

    return getFallbackValue(value)
}

const variantsMapToAtRulesConverters: Record<VariantType, (variantData: VariantData) => CssAtRule> = {
    breakpoints: ({id, max, canvasSize}: VariantData): CssAtRule => ({
        identifier: 'media',
        queries: {
            id,
            maxWidth: max,
            canvasSize
        },
        rules: []
    }),
    presets: ({id}: VariantData): CssAtRule => ({
        identifier: 'preset',
        queries: {
            id
        },
        rules: []
    })
}

export const convertVariantsMapToCssAstRules = (variantsMap?: VariantsMap): AtRulesMap => {
    const atRulesMap: Record<string, CssAtRule> = {}
    _.forOwn(variantsMap, (variantsData: VariantsData | undefined, variantKey: string) => {
        _.forOwn(variantsData, (variantData: VariantData, variantId: string) => {
            atRulesMap[variantId] = variantsMapToAtRulesConverters[variantKey as VariantType](variantData)
        })
    })

    return atRulesMap
}

const getDataItemConditions = (combinedVariants: string, atRulesMap: AtRulesMap): DataItemConditions => {
    const variants = combinedVariants
        .split(VARIANTS_DELIMITER)
        .filter((variant: string) => variant !== DEFAULT_VARIANT_KEY)
    const atRuleIndex = variants.findIndex((variant: string) => atRulesMap[variant])

    if (atRuleIndex !== -1) {
        const [atRuleId] = variants.splice(atRuleIndex, 1)
        return {
            atRule: atRulesMap[atRuleId],
            variants
        }
    }

    return {
        variants
    }
}

const createCssRule = (
    id: string,
    declarations: CssDeclarationBlock,
    variants: DataItemConditions['variants']
): CssRule => ({
    selector: [`#${id}`, ...variants].join('.'),
    declarations
})

const appendRule = (rule: CssRule, cssFileBuilder: CssFileBuilder) => {
    cssFileBuilder.startBlock(rule.selector)
    _.forOwn(rule.declarations, (value: CssValue, property: CssProperty) =>
        cssFileBuilder.writeProperty(property, value)
    )
    cssFileBuilder.endBlock()
}

const buildAtRule = ({identifier, queries}: CssAtRule): string => {
    const cssQueries: string[] = []
    _.forOwn(queries, (value: any, key: string) => {
        if (value !== undefined) {
            const queryInfo = AT_RULE_QUERY_AST_TO_CSS_MAP[identifier][key]
            cssQueries.push(`(${queryInfo.key}: ${value}${queryInfo.valueInfo?.suffix ?? ''})`)
        }
    })
    return `@${identifier} ${cssQueries.join(' and ')}`
}

const appendAtRule = (atRule: CssAtRule, cssFileBuilder: CssFileBuilder) => {
    cssFileBuilder.startBlock(buildAtRule(atRule))
    for (const rule of atRule.rules) {
        appendRule(rule, cssFileBuilder)
    }
    cssFileBuilder.endBlock()
}

export const getDeclarationsByDataItem = (dataItem: ResolvedDataItem, namespace: string): CssDeclarationBlock => {
    const declarationBlock: Record<string, CssValue> = {}
    forEachDataItemProperty(
        dataItem,
        namespace,
        dataItem.type!,
        ({val, path, pathArr, metadata}: ForEachObjectLeafCbArgs) => {
            if (_.isPlainObject(val) && _.isEmpty(val)) {
                return
            }

            const cssProperty =
                SCHEMA_PATH_TO_CSS_PROPERTIES[namespace]?.[dataItem.type!]?.[path] ??
                buildDefaultCssProperty(namespace, pathArr)
            const types = metadata?.types ?? [{type: DEFAULT_SERIALIZER_TYPE}]
            declarationBlock[cssProperty] = serializeValue(val, types)
        }
    )

    return declarationBlock
}

export const getDataItemDeclarationsByVariantMap = (
    namespace: string,
    dataItem: VariantSplit
): CssDeclarationBlockByVariantMap => {
    const declarationBlockByVariantMap: CssDeclarationBlockByVariantMap = {}
    if (dataItem.default) {
        declarationBlockByVariantMap[DEFAULT_VARIANT_KEY] = getDeclarationsByDataItem(dataItem.default, namespace)
    }

    if (dataItem.variants) {
        for (const {value, conditions} of dataItem.variants) {
            const key = conditions.map(stripHashIfExists).join(VARIANTS_DELIMITER)
            declarationBlockByVariantMap[key] = getDeclarationsByDataItem(value, namespace)
        }
    }

    return declarationBlockByVariantMap
}

export const getComponentDeclarationsByVariantMap = (
    component: ComponentExportStructure
): CssDeclarationBlockByVariantMap => {
    const declarationBlockByVariantMap: CssDeclarationBlockByVariantMap = {}

    for (const namespace of DATA_TYPES) {
        const dataItem = component[namespace]
        if (dataItem) {
            const dataItemRules = getDataItemDeclarationsByVariantMap(namespace, dataItem)
            _.merge(declarationBlockByVariantMap, dataItemRules)
        }
    }

    return declarationBlockByVariantMap
}

export const convertCssAstToFile = (cssAst: CssAst): WmlFileContent => {
    const cssFileBuilder = new CssFileBuilder()
    for (const block of cssAst) {
        if (isCssRule(block)) {
            appendRule(block, cssFileBuilder)
        } else {
            appendAtRule(block, cssFileBuilder)
        }
    }

    return cssFileBuilder.build()
}

export const generateCssAst = ({components, variantsMap}: PartialComponentExport['style']): CssAst => {
    const componentsDeclarations = _.mapValues(components, getComponentDeclarationsByVariantMap)
    const rules: CssRule[] = []
    const atRulesMap: Record<string, CssAtRule> = convertVariantsMapToCssAstRules(variantsMap)
    _.forOwn(componentsDeclarations, (declarationsMap: CssDeclarationBlockByVariantMap, componentId: string) => {
        _.forOwn(declarationsMap, (declarations: CssDeclarationBlock, variants: string) => {
            const dataItemConditions = getDataItemConditions(variants, atRulesMap)
            const rule = createCssRule(componentId, declarations, dataItemConditions.variants)
            const {atRule} = dataItemConditions
            const rulesList = atRule?.rules ?? rules
            rulesList.push(rule)
        })
    })

    return [...rules, ...Object.values(atRulesMap)]
}

export const generateCss = (componentExport: PartialComponentExport['style']): WmlFileContent =>
    convertCssAstToFile(generateCssAst(componentExport))

export const generateCssFile = (componentExport: PartialComponentExport['style']): CssFile => ({
    type: 'css',
    content: generateCss(componentExport)
})
